import React, { useMemo } from 'react';
import { BarChart3, Info } from 'lucide-react';
import { CalculatedMetrics } from '../../types';

interface DistributionAnalysisProps {
  data: CalculatedMetrics[];
}

interface HistogramBin {
  range: string;
  count: number;
  percentage: number;
  color: string;
}

interface DistributionStats {
  mean: number;
  median: number;
  mode: number;
  stdDev: number;
  skewness: number;
  kurtosis: number;
}

export const DistributionAnalysis: React.FC<DistributionAnalysisProps> = ({ data }) => {
  const { histogram, stats } = useMemo(() => {
    if (data.length === 0) {
      return {
        histogram: [],
        stats: {
          mean: 0,
          median: 0,
          mode: 0,
          stdDev: 0,
          skewness: 0,
          kurtosis: 0
        }
      };
    }

    const spikeValues = data
      .map(d => (d['Spike %'] || 0) * 100)
      .sort((a, b) => a - b);

    const mean = spikeValues.reduce((sum, val) => sum + val, 0) / spikeValues.length;
    const median = spikeValues[Math.floor(spikeValues.length / 2)];

    const variance = spikeValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / spikeValues.length;
    const stdDev = Math.sqrt(variance);

    const skewness = spikeValues.reduce((sum, val) => sum + Math.pow((val - mean) / stdDev, 3), 0) / spikeValues.length;
    const kurtosis = spikeValues.reduce((sum, val) => sum + Math.pow((val - mean) / stdDev, 4), 0) / spikeValues.length - 3;

    const binCount = Math.min(20, Math.ceil(Math.sqrt(spikeValues.length)));
    const min = Math.floor(spikeValues[0] / 10) * 10;
    const max = Math.ceil(spikeValues[spikeValues.length - 1] / 10) * 10;
    const binSize = (max - min) / binCount;

    const bins: HistogramBin[] = [];
    for (let i = 0; i < binCount; i++) {
      const rangeStart = min + i * binSize;
      const rangeEnd = min + (i + 1) * binSize;
      const count = spikeValues.filter(v => v >= rangeStart && v < rangeEnd).length;

      let color = '#6B7280';
      if (rangeStart >= 200) color = '#8B0000';
      else if (rangeStart >= 100) color = '#FF8C00';
      else if (rangeStart >= 50) color = '#FFD700';
      else if (rangeStart >= 30) color = '#32CD32';

      bins.push({
        range: `${rangeStart.toFixed(0)}-${rangeEnd.toFixed(0)}`,
        count,
        percentage: (count / spikeValues.length) * 100,
        color
      });
    }

    const modeBin = bins.reduce((max, bin) => bin.count > max.count ? bin : max, bins[0]);
    const mode = parseFloat(modeBin.range.split('-')[0]);

    return {
      histogram: bins,
      stats: {
        mean,
        median,
        mode,
        stdDev,
        skewness,
        kurtosis
      }
    };
  }, [data]);

  const maxCount = Math.max(...histogram.map(b => b.count), 1);

  return (
    <div className="bg-gray-900/30 border border-gray-600 rounded-lg p-4 sm:p-6">
      <div className="flex items-center space-x-2 sm:space-x-3 mb-6">
        <div className="p-1.5 sm:p-2 bg-green-400/10 rounded-lg">
          <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold text-white">Analisi della Distribuzione</h4>
          <p className="text-xs sm:text-sm text-gray-400">
            Distribuzione statistica dei valori di spike
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">Media</p>
          <p className="text-lg font-bold text-blue-400">{stats.mean.toFixed(2)}%</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">Mediana</p>
          <p className="text-lg font-bold text-green-400">{stats.median.toFixed(2)}%</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">Moda</p>
          <p className="text-lg font-bold text-yellow-400">{stats.mode.toFixed(0)}%</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">Dev. Std</p>
          <p className="text-lg font-bold text-purple-400">{stats.stdDev.toFixed(2)}%</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">Asimmetria</p>
          <p className="text-lg font-bold text-orange-400">{stats.skewness.toFixed(2)}</p>
        </div>
        <div className="bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-400 mb-1">Curtosi</p>
          <p className="text-lg font-bold text-red-400">{stats.kurtosis.toFixed(2)}</p>
        </div>
      </div>

      <div className="mb-6">
        <h5 className="text-sm font-medium text-white mb-4">Istogramma di Distribuzione</h5>
        <div className="space-y-2">
          {histogram.map((bin, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-24 text-xs text-gray-400 font-medium">
                {bin.range}%
              </div>
              <div className="flex-1 relative h-8 bg-gray-800 rounded overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full transition-all duration-300"
                  style={{
                    width: `${(bin.count / maxCount) * 100}%`,
                    backgroundColor: bin.color
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-end pr-2">
                  <span className="text-xs font-medium text-white">
                    {bin.count} ({bin.percentage.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-blue-400/10 border border-blue-400/30 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-blue-400">Interpretazione</h5>
            <ul className="text-xs text-blue-300 mt-2 space-y-1">
              <li>• <strong>Asimmetria {'>'} 0:</strong> La distribuzione è spostata verso valori bassi (più spike piccoli)</li>
              <li>• <strong>Asimmetria {'<'} 0:</strong> La distribuzione è spostata verso valori alti (più spike grandi)</li>
              <li>• <strong>Curtosi {'>'} 0:</strong> Distribuzione con code pesanti (più valori estremi)</li>
              <li>• <strong>Curtosi {'<'} 0:</strong> Distribuzione con code leggere (meno valori estremi)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
